import { Button, Card, Empty, Result, Space, Spin, Typography } from 'antd';
import { useListTeamClients } from 'client/modules/external-api/hooks/queries/external-api';
import { AntPageLayoutWithHeader } from 'client/modules/permissions/components/AntPageLayoutWithHeader';
import { Link, useParams } from 'react-router-dom';
import {
  ApiApplication,
  ListTeamClientsResponse,
} from '@cbinsights/externalapiservice/externalapiservice';
import { LeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { ReactNode } from 'react';
import { CreateClientButton } from 'client/modules/external-api/components/CreateClientButton';
import useGetTeamQuery from 'client/modules/team/services/hooks/useGetTeamQuery';
import { DataFeedRoutes } from '../routesHelper';
import { TeamDataFeedConfigList } from '../components/TeamDataFeedConfigList';

const { Title, Text } = Typography;
const NoClientScreen = ({
  idTeam,
  teamCredits,
}: {
  idTeam: number;
  teamCredits: number;
}) => {
  const { data: teamResponse, isLoading } = useGetTeamQuery({
    enabled: true,
    params: { id_team: idTeam },
  });

  return (
    <div className="flex justify-center items-center h-screen bg-gray-100">
      <Card className="max-w-md text-center shadow-md">
        <Space direction="vertical" size="large">
          <Empty description={false} />
          <Title level={4}>No client available</Title>
          <Text type="secondary">
            To continue, create a new client for the API.
          </Text>
          <Space>
            <Link to={DataFeedRoutes.DataFeeds}>
              <Button
                type="primary"
                className="bg-red-600 text-white border-red-400 hover:border-red-400 visited:border-red-400 hover:bg-red-700 visited:bg-red-700"
              >
                Cancel
              </Button>
            </Link>
            <CreateClientButton
              idTeam={idTeam}
              name={teamResponse?.team?.name || ''}
              disabled={isLoading}
              preselectedApplication={ApiApplication.API_APPLICATION_DATAFEEDS}
              initialCredits={teamCredits}
            />
          </Space>
        </Space>
      </Card>
    </div>
  );
};

export const pageTitle = (
  teamClientsData: ListTeamClientsResponse | undefined,
  isLoading: boolean
): ReactNode => {
  if (teamClientsData === undefined || isLoading) {
    return <LoadingOutlined />;
  }

  return `${teamClientsData?.clients[0]?.team_name} data feeds`;
};

export const TeamDataFeedPage = () => {
  const { idTeam } = useParams<{ idTeam: string }>();
  const { data: teamClientData, isLoading } = useListTeamClients(
    Number(idTeam)
  );

  if (!idTeam || Number.isNaN(Number(idTeam))) {
    return <Result status="404" title="Team not found" />;
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spin size="large" />
      </div>
    );
  }

  const credits = Number(teamClientData?.clients[0]?.available_credits) || 0;

  if (
    teamClientData?.clients?.filter(
      (el) => el.api_application === ApiApplication.API_APPLICATION_DATAFEEDS
    ).length === 0
  ) {
    return <NoClientScreen idTeam={Number(idTeam)} teamCredits={credits} />;
  }

  return (
    <AntPageLayoutWithHeader
      title={
        <Space>
          <Link to={DataFeedRoutes.DataFeeds}>
            <Button type="link" icon={<LeftOutlined />} />
          </Link>
          {pageTitle(teamClientData, isLoading)}
        </Space>
      }
      extra={
        <Link
          to={`${DataFeedRoutes.DataFeed}/${idTeam}/${DataFeedRoutes.CreateDataFeed}`}
        >
          <Button type="primary" className="bg-blue-500">
            Create data feed
          </Button>
        </Link>
      }
    >
      <TeamDataFeedConfigList idTeam={idTeam} />
    </AntPageLayoutWithHeader>
  );
};
